/**
 * Account Context Provider
 *
 * TASK-085: Account Context Management + Password Authentication
 * TASK-264: Multi-User RBAC Support
 *
 * Centralized account and user state management for multi-tenant application.
 * Provides account and user information globally to all components.
 *
 * Features:
 * - Load account and user from JWT token on app mount
 * - Global account/user state accessible via useAccount() hook
 * - Role-based access control helpers (isAdmin, isAdvisor, hasPermission)
 * - Account switching support
 * - Auto-logout on token expiry (handled by httpClient)
 *
 * @module contexts/AccountContext
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { decodeJWT } from '../utils/jwtDecoder';
import { httpClient } from '../lib/httpClient';
import { logger } from '../utils/logger';
import { purgeDockScopeStorage } from '../stores/createChatStore';

/**
 * User information interface (TASK-264)
 */
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  role: 'admin' | 'advisor' | 'viewer' | 'compliance_officer';
}

/**
 * Account information interface
 */
export interface Account {
  id: string;
  name: string;
  email: string;
  scenario: string;
  isDemo: boolean;
  preferred_language?: string; // TASK-167: Language preference from backend
  // TASK-428: per-tenant UI feature flags (backed by accounts.features JSONB,
  // delivered via JWT claim — see Phase 6). Optional/permissive until then.
  features?: Record<string, boolean>;
}

/**
 * Role-based permissions (TASK-264, TASK-286)
 */
const ROLE_PERMISSIONS: Record<string, Set<string>> = {
  admin: new Set([
    'users:read', 'users:write', 'users:delete',
    'clients:read', 'clients:write', 'clients:delete',
    'meetings:read', 'meetings:write', 'meetings:delete',
    'portfolios:read', 'portfolios:write',
    'kyc:read', 'kyc:write', 'kyc:approve',
    'aml:read', 'aml:write',
    'settings:read', 'settings:write',
    // TASK-286: Agentic Chat permissions
    'chat:read', 'chat:write', 'chat:navigate', 'chat:export',
  ]),
  advisor: new Set([
    'clients:read', 'clients:write',
    'meetings:read', 'meetings:write',
    'portfolios:read', 'portfolios:write',
    'kyc:read', 'kyc:write',
    'aml:read', 'aml:write',
    // TASK-286: Agentic Chat permissions
    'chat:read', 'chat:write', 'chat:navigate',
  ]),
  viewer: new Set([
    'clients:read',
    'meetings:read',
    'portfolios:read',
    'kyc:read',
    'aml:read',
    // TASK-286: Agentic Chat permissions (read-only)
    'chat:read',
  ]),
  // TASK-372: Compliance Officer role
  compliance_officer: new Set([
    'kyc:read', 'kyc:review', 'kyc:approve', 'kyc:reject', 'kyc:comment',
    'transactions:read',
    'documents:read',
    'document_requests:create',
    'clients:read',
    'portfolios:read',
    'chat:read',
  ]),
};

/**
 * Account context value interface
 */
export interface AccountContextValue {
  /** Current account or null if not authenticated */
  account: Account | null;

  /** Current user or null if not authenticated (TASK-264) */
  user: User | null;

  /** Set the current account (used after login) */
  setAccount: (account: Account) => void;

  /** Set the current user (used after login) (TASK-264) */
  setUser: (user: User) => void;

  /** Clear the account and logout */
  clearAccount: () => void;

  /** Convenience flag for authentication status */
  isAuthenticated: boolean;

  /** Check if current user is admin (TASK-264) */
  isAdmin: boolean;

  /** Check if current user is advisor (TASK-264) */
  isAdvisor: boolean;

  /** Check if current user is viewer (TASK-264) */
  isViewer: boolean;

  /** Check if current user is compliance officer (TASK-372) */
  isComplianceOfficer: boolean;

  /** Check if current user has specific permission (TASK-264) */
  hasPermission: (permission: string) => boolean;
}

/**
 * Account context - provides account state to entire app
 */
const AccountContext = createContext<AccountContextValue | undefined>(undefined);

/**
 * TASK-923: the viewer's coarse role, or `undefined` when there is no provider.
 *
 * `useAccount()` THROWS outside a provider, deliberately — a page that needs the account and
 * has none is a bug. But the signal surfaces need only the role, and only to decide which
 * buttons to render. Crashing a whole alerts page because a presentational gate could not
 * find its context is a far worse failure than the one the guard protects against, and it
 * would make every component that asks depend on a provider that fetches `/api/settings` on
 * mount.
 *
 * Undefined is a safe answer: `policyPermits` fails CLOSED on an unknown role, so the
 * fallback is "do not offer the compliance-only control", never "offer it to everyone".
 */
export function useViewerRole(): string | undefined {
  return useContext(AccountContext)?.user?.role;
}

/**
 * Account Provider Props
 */
interface AccountProviderProps {
  children: ReactNode;
}

/**
 * Account Provider Component
 *
 * Wraps the entire application to provide account and user context.
 * TASK-264: Added user state for RBAC support.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <AccountProvider>
 *       <YourApp />
 *     </AccountProvider>
 *   );
 * }
 * ```
 */
export function AccountProvider({ children }: AccountProviderProps) {
  const [account, setAccountState] = useState<Account | null>(null);
  const [user, setUserState] = useState<User | null>(null); // TASK-264

  /**
   * Load account and user from stored JWT token on mount
   * TASK-168: Also fetch settings to get preferred language
   * TASK-264: Also extract user information from JWT
   */
  useEffect(() => {
    const token = localStorage.getItem('auth_token');

    if (token) {
      try {
        // Decode JWT to extract account and user information
        const decoded = decodeJWT(token);

        // Fetch user settings to get preferred language (TASK-168)
        // TASK-923 review: the role is in the token that was JUST decoded, and everything below
        // waits on an awaited `/api/settings` fetch before `setUserState` runs. The signal
        // surfaces gate compliance controls on that role and fail CLOSED while it is unknown, so
        // a compliance officer watched escalate/resolve pop in one round-trip late on every hard
        // load — and a slow settings call widened that window arbitrarily. Publishing the decoded
        // user first closes it; the fetch below still refines the rest.
        if (decoded.user_id) {
          setUserState({
            id: decoded.user_id,
            email: decoded.email,
            firstName: decoded.first_name || undefined,
            lastName: decoded.last_name || undefined,
            role: decoded.role || 'advisor',
          });
        }

        const loadAccountWithSettings = async () => {
          let preferredLanguage: string | undefined;

          try {
            const settingsResponse = await httpClient.get('/api/settings');
            preferredLanguage = settingsResponse.data?.preferred_language || settingsResponse.preferred_language;

            if (import.meta.env.MODE === 'development') {
              logger.debug('User settings loaded on mount:', { preferred_language: preferredLanguage });
            }
          } catch (settingsError) {
            // Silent fail - settings are optional
            logger.warn('Failed to load user settings on mount:', settingsError);
          }

          // Persist account_id for X-Account-ID header (RLS)
          if (decoded.account_id) {
            localStorage.setItem('account_id', decoded.account_id);
          }

          // Populate account state from token claims + settings
          setAccountState({
            id: decoded.account_id,
            name: decoded.account_name,
            email: decoded.email,
            scenario: decoded.demo_scenario || 'unknown',
            isDemo: decoded.user_type === 'demo',
            preferred_language: preferredLanguage, // TASK-168: Include language preference
            features: decoded.features ?? {}, // TASK-428: per-tenant UI feature flags
          });

          // TASK-264: Populate user state from JWT claims
          // user_id in JWT is the real user UUID (not account_id)
          if (decoded.user_id) {
            setUserState({
              id: decoded.user_id,
              email: decoded.email,
              firstName: decoded.first_name || undefined,
              lastName: decoded.last_name || undefined,
              role: decoded.role || 'advisor', // Default to advisor for legacy tokens
            });

            if (import.meta.env.MODE === 'development') {
              logger.debug('User loaded from token:', decoded.user_id, 'role:', decoded.role);
            }
          }

          if (import.meta.env.MODE === 'development') {
            logger.debug('Account loaded from token:', decoded.email);
          }
        };

        loadAccountWithSettings();
      } catch (error) {
        logger.error('Failed to decode auth token:', error);

        // Clear invalid token
        localStorage.removeItem('auth_token');
        localStorage.removeItem('account_id');
        purgeDockScopeStorage(); // TASK-710

      }
    }
  }, []);

  /**
   * NOTE: No axios interceptor needed here.
   * The httpClient (lib/httpClient.ts:68-71) already has a JWT interceptor
   * that automatically adds Authorization header to all requests.
   *
   * Implementing it here would duplicate the interceptor and cause issues.
   */

  /**
   * Set account (used after successful login)
   */
  const setAccount = (newAccount: Account) => {
    setAccountState(newAccount);
    if (import.meta.env.MODE === 'development') {
      logger.debug('Account context updated:', newAccount.email);
    }
  };

  /**
   * Set user (used after successful login) (TASK-264)
   */
  const setUser = (newUser: User) => {
    setUserState(newUser);
    if (import.meta.env.MODE === 'development') {
      logger.debug('User context updated:', newUser.id, 'role:', newUser.role);
    }
  };

  /**
   * Clear account and logout
   *
   * Removes auth token and resets account and user state.
   * Triggers redirect to login (handled by httpClient on next API call).
   */
  const clearAccount = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('account_id');
    // TASK-710: dock conversations are keyed per account but sessionStorage
    // survives auth changes in the same tab — purge so the next login can
    // never see the previous account's threads.
    purgeDockScopeStorage();
    setAccountState(null);
    setUserState(null); // TASK-264: Also clear user
    if (import.meta.env.MODE === 'development') {
      logger.debug('Account and user cleared, logged out');
    }
  };

  /**
   * Check if user has specific permission (TASK-264)
   */
  const hasPermission = (permission: string): boolean => {
    if (!user?.role) return false;
    const permissions = ROLE_PERMISSIONS[user.role];
    return permissions?.has(permission) ?? false;
  };

  // RBAC convenience flags (TASK-264, TASK-372)
  const isAdmin = user?.role === 'admin';
  const isAdvisor = user?.role === 'advisor';
  const isViewer = user?.role === 'viewer';
  const isComplianceOfficer = user?.role === 'compliance_officer';

  // Context value
  const value: AccountContextValue = {
    account,
    user,
    setAccount,
    setUser,
    clearAccount,
    isAuthenticated: !!account,
    isAdmin,
    isAdvisor,
    isViewer,
    isComplianceOfficer,
    hasPermission,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

/**
 * Use Account Hook
 *
 * Access account context from any component.
 *
 * @throws Error if used outside AccountProvider
 *
 * @example
 * ```tsx
 * function DashboardPage() {
 *   const { account, isAuthenticated } = useAccount();
 *
 *   if (!isAuthenticated) {
 *     return <Navigate to="/login" />;
 *   }
 *
 *   return (
 *     <div>
 *       <h1>Welcome, {account.name}</h1>
 *       <p>Email: {account.email}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAccount(): AccountContextValue {
  const context = useContext(AccountContext);

  if (!context) {
    throw new Error(
      'useAccount must be used within AccountProvider. ' +
      'Make sure <AccountProvider> wraps your app in App.tsx'
    );
  }

  return context;
}

/**
 * Export AccountContext for advanced use cases
 * (e.g., using Context.Consumer directly)
 */
export { AccountContext };
